import { useTranslation } from "react-i18next"

import TiktokConnectCta from "@/components/social-accounts/tiktok-connect-cta"
import TiktokOAuthStartForm from "@/components/social-accounts/tiktok-oauth-start-form"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"

export interface TiktokReconnectAlertProps {
  /** Server-resolved Reconnect POST URL — same as Settings page's
   *  `reconnect_url`. The OKS-32 page renders this alert and supplies
   *  the URL from its own page props. */
  reconnect_url: string
  className?: string
}

/**
 * TiktokReconnectAlert — inline `<Alert>` shown on OKS-32's mission
 * submission form when the creator's TikTok token is `revoked` (or
 * refresh has failed).
 *
 * **Component lives here, but is consumed by OKS-32's submission
 * page** (cross-ticket coordination per FE TRD F12). When OKS-32 ships,
 * its page will import this component and supply `reconnect_url` +
 * (eventually) the sessionStorage form-state preservation behaviour
 * before/after the OAuth round-trip.
 *
 * UX Spec §1.C / §2:
 *   - `<Alert variant="warning">` (amber) — informs without alarming.
 *   - "Your TikTok connection needs refreshing before you can submit"
 *     copy.
 *   - Reconnect CTA = TikTok-styled `<TiktokConnectCta>` (same tokens
 *     as Settings + OKS-22 Step 3 — single component, never forked).
 *   - sessionStorage form-state preservation is OKS-32's responsibility
 *     (UX Spec §C "deferred to OKS-32"). This component does NOT
 *     introduce sessionStorage usage — Crew hard rule "No
 *     `localStorage` / `sessionStorage`" stays clean for OKS-23 itself.
 */
export function TiktokReconnectAlert({
  reconnect_url,
  className = "",
}: TiktokReconnectAlertProps) {
  const { t } = useTranslation()
  return (
    <Alert
      data-testid="tiktok-reconnect-alert"
      className={[
        // Amber tone — shadcn `<Alert>` ships `default` + `destructive`;
        // we paint amber via Tailwind utilities so the alert reads as a
        // warning without escalating to red.
        "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100",
        className,
      ].join(" ")}
    >
      <AlertTitle>{t("tiktok.reconnect_alert.title")}</AlertTitle>
      <AlertDescription className="flex flex-col gap-3">
        <span>{t("tiktok.reconnect_alert.body")}</span>
        {/* Reconnect — plain HTML <form> (NOT Inertia <Form>). BE
            302s to tiktok.com which only follows from a top-level
            browser nav. See `<TiktokOAuthStartForm>` JSDoc. */}
        <TiktokOAuthStartForm
          action={reconnect_url}
          data-testid="tiktok-reconnect-alert-form"
        >
          <TiktokConnectCta
            labelKey="tiktok.reconnect_alert.cta"
            processingLabelKey="signup.tiktok.cta_processing"
            data-testid="tiktok-reconnect-alert-cta"
          />
        </TiktokOAuthStartForm>
      </AlertDescription>
    </Alert>
  )
}

export default TiktokReconnectAlert
